/**
 * SWISS Admin — backend API client
 * =================================
 * Calls the DynamoDB-backed admin Lambda via HTTP API Gateway.
 * Auth: Cognito id_token in the Authorization header (JWT
 * authorizer verifies it at the edge).
 *
 * Public function signatures are unchanged from the previous
 * localStorage-backed mock — every existing UI call site keeps
 * working.
 */

import { config } from '../config/config';

const BASE = config.admin.apiUrl.replace(/\/+$/, '');

const getIdToken = () => localStorage.getItem(config.jwt.storageKey);

/**
 * Fire a synthetic browser event when the API rejects our token.
 * AuthContext listens for this and clears the local session so the
 * user lands on /login with a friendly banner instead of seeing the
 * raw "Admin API /admin/... → 401" error inside whatever page they
 * were on.
 *
 * Guarded so N parallel in-flight requests that all 401 don't trigger
 * N redirects / state-clears. Once the flag is set the user is on
 * their way to /login; the flag clears when they sign in again.
 */
let sessionExpiryFired = false;
const fireSessionExpired = (path) => {
  if (sessionExpiryFired) return;
  sessionExpiryFired = true;
  try {
    sessionStorage.setItem('swiss_login_reason', 'expired');
  } catch { /* sessionStorage may be unavailable */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('swiss:session-expired', { detail: { path } }));
  }
};

// Reset the guard when the auth context successfully signs the user
// back in. AuthContext calls this from completeCognitoSignIn so the
// next expiry cycle works cleanly.
export const resetSessionExpiryGuard = () => { sessionExpiryFired = false; };

/**
 * Demo session = the synthetic JWT minted by AuthContext.demoLogin.
 * The signature is the literal string "demo" (real JWTs end in a
 * base64-encoded signature), so we can identify these cheaply.
 *
 * The real backend's JWT authorizer will reject the demo token with
 * 401 by design — there's no Cognito user behind it. Without this
 * guard, every backend call from the demo dashboard would 401, fire
 * `swiss:session-expired`, and bounce the user back to /login. The
 * "Try Demo Portal" button would then trigger a redirect loop.
 *
 * Returning shape-appropriate empty data per endpoint lets the demo
 * dashboard render its empty-state UI ("No teachers yet", etc.)
 * without touching the network.
 */
const isDemoToken = (t) => typeof t === 'string' && t.endsWith('.demo');

// ── Demo content ──────────────────────────────────────────────
// The demo session has no backend, so these builders supply realistic
// sample data — kept here so the demo shows every feature (Anonymous
// Reporting, the SLA flag, NPS, etc.) populated rather than empty.
// Dates are relative to now so the demo never looks stale.
const hAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();
const hAhead = (h) => new Date(Date.now() + h * 3600000).toISOString();

const demoCouncil = () => [
  { id: 'cm_dm1', schoolId: 'sch_001', name: 'Anjali Mehta', email: 'anjali.mehta@dpsgurgaon.edu', phone: '+919876500001', role: 'counselor', joinedAt: hAgo(4000) },
  { id: 'cm_dm2', schoolId: 'sch_001', name: 'Rohan Verma', email: 'rohan.verma@dpsgurgaon.edu', phone: null, role: 'chair', joinedAt: hAgo(4000) },
  { id: 'cm_dm3', schoolId: 'sch_001', name: 'Priya Nair', email: 'priya.nair@dpsgurgaon.edu', phone: '+919876500003', role: 'teacher_rep', joinedAt: hAgo(3200) },
  { id: 'cm_dm4', schoolId: 'sch_001', name: 'Kavita Rao', email: 'kavita.rao@example.com', phone: null, role: 'parent_rep', joinedAt: hAgo(2400) },
  { id: 'cm_dm5', schoolId: 'sch_001', name: 'Sunil Das', email: 'sunil.das@dpsgurgaon.edu', phone: '+919876500005', role: 'counselor', joinedAt: hAgo(1200) },
];

const demoAnonReports = () => [
  {
    id: 'anr_dm1', schoolId: 'sch_001', submittedAt: hAgo(32), category: 'mental_health',
    description: 'A student in Class 9 has been writing things in their notebook about not wanting to be here and not seeing the point of anything. A classmate is worried and wanted someone to know.',
    severity: 'critical',
    severityRationale: 'Possible suicidal ideation in writing — needs an immediate wellbeing check by the counsellor.',
    severitySource: 'claude', severityModel: 'claude-haiku-4-5',
    suggestedActions: [
      'Treat this as urgent — brief the counsellor and wellness council chair today and follow the crisis-response protocol.',
      'Discreetly brief Class 9 teachers to watch for a withdrawn or low-mood student over the coming days.',
      'Use the clues in the report — Class 9, writing in a notebook — to work out which student it concerns.',
      'Once identified, have the counsellor arrange an immediate, gentle wellbeing check-in with that student.',
      'If anything is disclosed, follow the safeguarding protocol and plan with the counsellor how to involve the family.',
    ],
    suspectSpam: false, spamRationale: null,
    status: 'received',
    statusHistory: [{ status: 'received', at: hAgo(32), by: 'reporter', note: 'Submitted anonymously.' }],
    slaDueAt: hAgo(8), acknowledgedAt: null, resolvedAt: null, assignedTo: null,
    schoolYearLabel: '2025–2026', overdue: true,
  },
  {
    id: 'anr_dm2', schoolId: 'sch_001', submittedAt: hAgo(20), category: 'bullying',
    description: 'A few students in Class 7 keep teasing and excluding one boy at lunch — taking his things and calling him names. It has been happening for weeks.',
    severity: 'high',
    severityRationale: 'Sustained bullying and exclusion of one student over weeks — prompt council action needed.',
    severitySource: 'claude', severityModel: 'claude-haiku-4-5',
    suggestedActions: [
      'Ask Class 7 teachers and the lunch supervisor to discreetly observe lunchtime dynamics over the next few days.',
      'Use the report to identify the boy being targeted and the group of students involved.',
      'Once identified, arrange a low-key wellbeing check-in with the affected boy.',
      'Address the behaviour with the students involved and set clear expectations.',
      'Log the report and review in a week to confirm the teasing and exclusion have stopped.',
    ],
    suspectSpam: false, spamRationale: null,
    status: 'evaluating',
    statusHistory: [
      { status: 'received', at: hAgo(20), by: 'reporter', note: 'Submitted anonymously.' },
      { status: 'evaluating', at: hAgo(8), by: 'anjali.mehta@dpsgurgaon.edu', note: 'Counsellor speaking with the class teacher this week.' },
    ],
    slaDueAt: hAhead(28), acknowledgedAt: hAgo(8), resolvedAt: null, assignedTo: null,
    schoolYearLabel: '2025–2026', overdue: false,
  },
  {
    id: 'anr_dm3', schoolId: 'sch_001', submittedAt: hAgo(9), category: 'harassment',
    description: 'Some students are making unkind comments online about a classmate. It makes a few of us uncomfortable and we think someone should look into it.',
    severity: 'medium',
    severityRationale: 'Online unkind comments causing discomfort — concerning but not an immediate safety risk.',
    severitySource: 'claude', severityModel: 'claude-haiku-4-5',
    suggestedActions: [
      'Brief class teachers to watch for a student who seems affected by unkind online comments.',
      'Use the report to work out which class and which platform are most likely involved.',
      'Once identified, arrange a discreet wellbeing check-in with the targeted student.',
      'Run a class reminder on respectful online conduct and the digital-wellbeing policy.',
      'Log the report and review the situation again in a week.',
    ],
    suspectSpam: false, spamRationale: null,
    status: 'received',
    statusHistory: [{ status: 'received', at: hAgo(9), by: 'reporter', note: 'Submitted anonymously.' }],
    slaDueAt: hAhead(63), acknowledgedAt: null, resolvedAt: null, assignedTo: null,
    schoolYearLabel: '2025–2026', overdue: false,
  },
  {
    id: 'anr_dm4', schoolId: 'sch_001', submittedAt: hAgo(168), category: 'discrimination',
    description: 'A student felt left out of a group activity and thought it might be because of where their family is from. They wanted the school to be aware.',
    severity: 'low',
    severityRationale: 'A student felt excluded, possibly identity-related — worth a gentle follow-up.',
    severitySource: 'claude', severityModel: 'claude-haiku-4-5',
    suggestedActions: [
      'Ask class teachers to note any student who appears excluded during group activities.',
      'Review how activity groups are formed so they stay inclusive.',
      'Reinforce belonging and inclusion in upcoming homeroom sessions.',
      'Log the report and keep a watchful eye over the coming weeks.',
    ],
    suspectSpam: false, spamRationale: null,
    status: 'resolved',
    statusHistory: [
      { status: 'received', at: hAgo(168), by: 'reporter', note: 'Submitted anonymously.' },
      { status: 'evaluating', at: hAgo(150), by: 'priya.nair@dpsgurgaon.edu', note: 'Class teacher following up.' },
      { status: 'resolved', at: hAgo(120), by: 'priya.nair@dpsgurgaon.edu', note: 'Spoke with the group; activity grouping adjusted. Student is settled.' },
    ],
    slaDueAt: hAgo(48), acknowledgedAt: hAgo(150), resolvedAt: hAgo(120), assignedTo: null,
    schoolYearLabel: '2025–2026', overdue: false,
  },
  {
    id: 'anr_dm5', schoolId: 'sch_001', submittedAt: hAgo(96), category: 'other',
    description: 'test test just checking if this form works asdf',
    severity: 'low',
    severityRationale: 'No genuine concern described.',
    severitySource: 'claude', severityModel: 'claude-haiku-4-5',
    suggestedActions: [],
    suspectSpam: true, spamRationale: 'Reads like a test submission.',
    status: 'dismissed',
    statusHistory: [
      { status: 'received', at: hAgo(96), by: 'reporter', note: 'Submitted anonymously.' },
      { status: 'dismissed', at: hAgo(95), by: 'rohan.verma@dpsgurgaon.edu', note: 'Test submission — no action needed.' },
    ],
    slaDueAt: hAhead(24), acknowledgedAt: null, resolvedAt: hAgo(95), assignedTo: null,
    schoolYearLabel: '2025–2026', overdue: false,
  },
];

const DEMO_SCHOOL_YEAR = {
  startDate: '2025-06-01', endDate: '2026-03-31', label: '2025–2026',
  setBy: 'demo@mysafespaces.org', setByRole: 'school_admin', setAt: hAgo(4000), locked: true,
};

const demoAnonMetrics = () => ({
  schoolYear: DEMO_SCHOOL_YEAR,
  total: 5,
  byStatus: { received: 2, evaluating: 1, resolved: 1, dismissed: 1 },
  bySeverity: { critical: 1, high: 1, medium: 1, low: 2 },
  byCategory: { mental_health: 1, bullying: 1, harassment: 1, discrimination: 1, other: 1 },
  overdue: 1,
  avgAckHours: 13.5,
  avgResolveHours: 28,
  open: 3,
});

// Demo compliance report — a believable DPS Gurgaon, computed exactly
// the way the backend computeCompliance() does. SWISS-managed mandates
// take their status from the certification phase that tracks them;
// school-managed ones take it from admin-entered input. DPS Gurgaon
// has 3 of 5 phases complete and Teacher Labs in progress.
const DEMO_PHASE_STATUS = {
  onboarding: 'completed',
  teachers: 'completed',
  policy: 'completed',
  labs: 'in_progress',
  certified: 'not_started',
};

// School-managed mandates SWISS does not itself deliver — tracked by
// admin-entered status + evidence. Pre-seeded so the demo shows the
// feature populated; the demoStub PATCH handler mutates this in place.
let demoComplianceInputs = {
  nep_counsellor_provision: {
    status: 'met',
    note: 'Ms. Anjali Mehta (M.A. Psychology, RCI-registered) is the full-time school counsellor, appointed June 2023.',
    updatedBy: 'principal@dpsgurgaon.edu', updatedAt: hAgo(720),
  },
  nep_counselling_all: {
    status: 'met',
    note: 'Weekly counselling slots open to every class; drop-in hours Mon–Fri 1–3 PM, and class teachers can refer students.',
    updatedBy: 'principal@dpsgurgaon.edu', updatedAt: hAgo(720),
  },
  cbse_career_counselling: {
    status: 'met',
    note: 'Structured career counselling for Classes 9–12 — a psychometric assessment each term plus one-to-one sessions with the counsellor.',
    updatedBy: 'principal@dpsgurgaon.edu', updatedAt: hAgo(700),
  },
  cbse_parent_sensitisation: {
    status: 'partial',
    note: 'Two parent wellbeing sessions held this year; the calendar for the remaining terms is being finalised with the council.',
    updatedBy: 'principal@dpsgurgaon.edu', updatedAt: hAgo(300),
  },
};

const demoCompliance = () => {
  const PHASE_KEYS = ['onboarding', 'teachers', 'policy', 'labs', 'certified'];
  const PHASE_LABELS = {
    onboarding: 'Onboarding', teachers: 'Teacher Training',
    policy: 'Policy Review', labs: 'Teacher Labs', certified: 'Certified',
  };
  const FRAMEWORK_LABELS = {
    cbse: 'CBSE Circular TRG-03/2026 & Affiliation Bye-Laws',
    supreme: 'Supreme Court Judgment dated 25.07.2025 (Criminal Appeal No. 3177/2025)',
    nep: 'National Education Policy 2020',
  };
  const toStatus = (ps) =>
    (ps === 'completed' ? 'met' : (ps === 'in_progress' || ps === 'scheduled' ? 'partial' : 'not_met'));
  const certStr = '152 of 168 teaching staff certified through SWISS Gatekeeper training.';

  // SWISS-managed defs carry a phaseKey; school-managed defs carry
  // source:'school' + a prompt and take their status from an
  // admin-entered input — exactly like the backend computeCompliance().
  const defs = [
    { id: 'cbse_staff_training', framework: 'cbse', phaseKey: 'teachers',
      title: 'Mandatory staff mental-health training, twice yearly, by certified professionals',
      reference: 'CBSE Circular TRG-03/2026 ¶1', detail: certStr },
    { id: 'cbse_capacity_building', framework: 'cbse', phaseKey: 'teachers',
      title: 'Domain-III Capacity Building Programmes for teaching staff',
      reference: 'CBSE Circular TRG-03/2026 — Domain III CBP', detail: certStr },
    { id: 'cbse_parent_sensitisation', framework: 'cbse', source: 'school',
      title: 'Regular parent & guardian sensitisation on student mental health',
      reference: 'CBSE Circular TRG-03/2026 ¶2',
      prompt: 'Record the parent & guardian sensitisation programme the school runs — sessions held, how often, and the topics covered.' },
    { id: 'cbse_career_counselling', framework: 'cbse', source: 'school',
      title: 'Structured career counselling by a qualified counsellor',
      reference: 'CBSE Circular TRG-03/2026 ¶5',
      prompt: 'Record the qualified counsellor delivering structured career counselling, and how students access it.' },
    { id: 'cbse_annual_report', framework: 'cbse', phaseKey: 'policy',
      title: 'Anonymous records & annual wellness report maintained',
      reference: 'CBSE Circular TRG-03/2026 ¶3',
      detail: 'Reporting templates in the SWISS Policies Toolkit — 4/4 adopted; anonymous reporting live.' },
    { id: 'cbse_holistic_dev', framework: 'cbse', phaseKey: 'labs',
      title: 'Co-curricular focus & a broader sense of identity beyond marks',
      reference: 'CBSE Circular TRG-03/2026 ¶4',
      detail: 'Delivered through SWISS quarterly Teacher Labs.' },
    { id: 'sc_training_hours', framework: 'supreme', phaseKey: 'teachers',
      title: '12+ hours of mental-health training per staff member, per year',
      reference: 'Supreme Court Judgment 25.07.2025 — Guideline VI',
      detail: `SWISS delivers 12+ hrs/year (Gatekeeper 4 hrs + quarterly Labs 8 hrs). ${certStr}` },
    { id: 'sc_psych_first_aid', framework: 'supreme', phaseKey: 'teachers',
      title: 'Psychological First Aid — warning signs, self-harm response, referral',
      reference: 'Supreme Court Judgment 25.07.2025 — Guideline VI',
      detail: 'Psychological First Aid, warning-sign identification and referral are core to the SWISS Gatekeeper curriculum.' },
    { id: 'sc_inclusive_engagement', framework: 'supreme', phaseKey: 'labs',
      title: 'Sensitive, inclusive engagement with all students, incl. marginalised groups',
      reference: 'Supreme Court Judgment 25.07.2025 — Guideline VII',
      detail: 'Covered by the inclusive-practice modules in SWISS quarterly Teacher Labs.' },
    { id: 'sc_anonymised_records', framework: 'supreme', phaseKey: 'policy',
      title: 'Anonymised wellness records maintained',
      reference: 'Supreme Court Judgment 25.07.2025 — Guideline X',
      detail: 'Anonymised records & reporting templates adopted at the Policy Review phase; anonymous reporting live.' },
    { id: 'sc_crisis_referral', framework: 'supreme', phaseKey: 'certified',
      title: 'Crisis intervention & referral protocols',
      reference: 'Supreme Court Judgment 25.07.2025 — Guidelines V & VIII',
      detail: 'Crisis response & referral protocols are finalised at full SWISS certification; incident channel live.' },
    { id: 'nep_counselling_all', framework: 'nep', source: 'school',
      title: 'Academic & career counselling available to all students',
      reference: 'NEP 2020 Clause 12.4',
      prompt: 'Record how academic & career counselling is made available to every student, not only those who seek it out.' },
    { id: 'nep_counsellor_provision', framework: 'nep', source: 'school',
      title: 'Counsellor / Wellness Teacher appointed',
      reference: 'NEP 2020 Clause 12.4 · CBSE Affiliation Bye-Laws 2.4.12',
      prompt: 'Record the appointed counsellor / wellness teacher — name, qualification, and appointment date.' },
    { id: 'nep_inclusion_dignity', framework: 'nep', phaseKey: 'policy',
      title: 'Staff & students sensitised to inclusion, equity, dignity & privacy',
      reference: 'NEP 2020 Clause 6.19',
      detail: 'Anchored in the wellness policies — 4/4 adopted; 152/168 staff trained.' },
    { id: 'nep_wellbeing_support', framework: 'nep', phaseKey: 'labs',
      title: 'Physical, psychological & emotional wellbeing support',
      reference: 'NEP 2020 Clause 12.4',
      detail: 'Delivered through the SWISS wellbeing programme and quarterly Teacher Labs.' },
  ];

  const requirements = defs.map((d) => {
    if (d.source === 'school') {
      const input = demoComplianceInputs[d.id] || null;
      return {
        id: d.id, framework: d.framework, title: d.title, reference: d.reference,
        source: 'school', phaseKey: null, phaseLabel: null,
        status: input?.status || 'not_met',
        detail: input?.note || d.prompt,
        prompt: d.prompt,
        input,
      };
    }
    return {
      ...d,
      source: 'swiss',
      phaseLabel: PHASE_LABELS[d.phaseKey] || d.phaseKey,
      status: toStatus(DEMO_PHASE_STATUS[d.phaseKey]),
      input: null,
    };
  });
  const score = (s) => (s === 'met' ? 1 : s === 'partial' ? 0.5 : 0);
  const pct = (items) => (items.length
    ? Math.round((items.reduce((a, r) => a + score(r.status), 0) / items.length) * 100) : 0);

  const byFramework = {};
  for (const fw of ['cbse', 'supreme', 'nep']) {
    const items = requirements.filter((r) => r.framework === fw);
    byFramework[fw] = {
      label: FRAMEWORK_LABELS[fw],
      count: items.length,
      met: items.filter((r) => r.status === 'met').length,
      percent: pct(items),
    };
  }
  const byPhase = PHASE_KEYS.map((k) => {
    const items = requirements.filter((r) => r.phaseKey === k);
    return {
      key: k,
      label: PHASE_LABELS[k],
      phaseStatus: DEMO_PHASE_STATUS[k],
      count: items.length,
      met: items.filter((r) => r.status === 'met').length,
      requirements: items.map((r) => ({
        id: r.id, title: r.title, framework: r.framework, reference: r.reference, status: r.status,
      })),
    };
  });
  const overallPercent = pct(requirements);
  const STAGES = [
    { min: 90, stage: 'Fully compliant' },
    { min: 70, stage: 'Substantially compliant' },
    { min: 40, stage: 'Developing' },
    { min: 15, stage: 'Foundational' },
    { min: 0, stage: 'Getting started' },
  ];
  return {
    schoolId: 'sch_001', schoolName: 'DPS Gurgaon', generatedAt: new Date().toISOString(),
    overallPercent,
    stage: (STAGES.find((x) => overallPercent >= x.min) || STAGES[4]).stage,
    phasesCompleted: PHASE_KEYS.filter((k) => DEMO_PHASE_STATUS[k] === 'completed').length,
    totalPhases: PHASE_KEYS.length,
    metCount: requirements.filter((r) => r.status === 'met').length,
    partialCount: requirements.filter((r) => r.status === 'partial').length,
    notMetCount: requirements.filter((r) => r.status === 'not_met').length,
    byFramework, byPhase, requirements,
    disclaimer: "This report maps your school's SWISS programme data to the requirements of CBSE Circular TRG-03/2026, the Supreme Court of India's Judgment dated 25.07.2025 in Criminal Appeal No. 3177/2025, and the National Education Policy 2020. CBSE Circular TRG-03/2026 operationalises the Supreme Court's directions, so one piece of SWISS evidence can satisfy requirements under more than one framework. This is a self-assessment aid to track progress — not an official compliance certification, audit finding, or legal advice. Confirm your school's specific obligations against the current circulars and with your compliance and legal advisors.",
  };
};

// Demo certification phases — driven by the same DEMO_PHASE_STATUS map
// as demoCompliance(), so the demo's Progress page and compliance
// report tell exactly the same story (the point of the sync feature).
const demoPhases = () => {
  const meta = {
    onboarding: { sched: hAgo(4200), done: hAgo(3900), notes: 'Kick-off complete, MoU signed, wellness council formed.' },
    teachers:   { sched: hAgo(3700), done: hAgo(1700), notes: '152 of 168 teaching staff certified through SWISS Gatekeeper training.' },
    policy:     { sched: hAgo(1600), done: hAgo(840),  notes: 'All four SWISS wellness policies reviewed and adopted by the council.' },
    labs:       { sched: hAgo(220),  done: null,       notes: 'Quarterly Teacher Labs underway — 2 of 4 wellbeing sessions held.' },
    certified:  { sched: null,       done: null,       notes: '' },
  };
  const out = {};
  for (const [k, m] of Object.entries(meta)) {
    const status = DEMO_PHASE_STATUS[k];
    out[k] = {
      status,
      scheduledAt: m.sched,
      completedAt: status === 'completed' ? m.done : null,
      notes: m.notes,
    };
  }
  return out;
};

// Demo in-person sessions — one closed Gatekeeper cohort with
// attendance + feedback, one open lab. surveyToken 'demo' so the QR
// renders without a live backend.
const demoSessions = () => {
  const stat = (attendees) => {
    const ratings = attendees.map((a) => a.rating).filter((r) => Number.isFinite(r));
    const recs = attendees.map((a) => a.recommend).filter((r) => Number.isFinite(r));
    const promoters = recs.filter((r) => r >= 9).length;
    const detractors = recs.filter((r) => r <= 6).length;
    return {
      count: attendees.length,
      avgRating: ratings.length ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10 : null,
      nps: recs.length ? Math.round(((promoters - detractors) / recs.length) * 100) : null,
      recommendCount: recs.length,
    };
  };
  const gkAttendees = [
    { teacherId: 'dt1', name: 'Anjali Mehta', rating: 5, recommend: 10, comment: 'Excellent — the role-play scenarios were very practical.', submittedAt: hAgo(120) },
    { teacherId: 'dt2', name: 'Rohan Verma', rating: 4, recommend: 9, comment: 'Good session. Would like more time on referral pathways.', submittedAt: hAgo(120) },
    { teacherId: 'dt3', name: 'Priya Nair', rating: 5, recommend: 9, comment: '', submittedAt: hAgo(119) },
  ];
  const labAttendees = [
    { teacherId: 'dt4', name: 'Sunil Das', rating: 4, recommend: 8, comment: 'Useful wellbeing check-in techniques.', submittedAt: hAgo(20) },
  ];
  return [
    {
      id: 'ses_dm1', schoolId: 'sch_001', programType: 'gatekeeper', programLabel: 'Gatekeeper course',
      courseSlug: 'swiss-gatekeeper-foundations', workshopId: null,
      title: 'Gatekeeper Foundations — in-person cohort', facilitator: 'Dr. Meera Krishnan',
      scheduledAt: hAgo(126), audience: 'teachers', surveyToken: 'demo', status: 'completed',
      createdAt: hAgo(150), createdBy: 'demo@mysafespaces.org',
      attendees: gkAttendees, stats: stat(gkAttendees),
      attendance: { teachersPresent: 3, totalTeachers: 4, percent: 75, markedAt: hAgo(118) },
    },
    {
      id: 'ses_dm2', schoolId: 'sch_001', programType: 'lab', programLabel: 'Teacher Lab',
      courseSlug: null, workshopId: null,
      title: 'Q2 Teacher Wellbeing Lab', facilitator: 'Kavita Rao',
      scheduledAt: hAgo(22), audience: 'teachers', surveyToken: 'demo', status: 'open',
      createdAt: hAgo(48), createdBy: 'demo@mysafespaces.org',
      attendees: labAttendees, stats: stat(labAttendees),
    },
  ];
};

const demoStub = (path, init = {}) => {
  const method = (init.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    // School-year save in the demo — echo back a real schoolYear shape
    // so the card reflects it immediately.
    if (/\/admin\/schools\/[^/]+\/school-year/.test(path)) {
      let p = {};
      try { p = init.body ? JSON.parse(init.body) : {}; } catch { p = {}; }
      const yr = (d) => (d ? new Date(`${d}T00:00:00`).getFullYear() : '');
      return {
        startDate: p.startDate || null,
        endDate: p.endDate || null,
        label: p.startDate ? `${yr(p.startDate)}–${yr(p.endDate)}` : 'demo',
        setBy: 'demo@mysafespaces.org', setByRole: 'school_admin',
        setAt: new Date().toISOString(), locked: true,
      };
    }
    // Intellicare chat in the demo — a canned, on-message reply that
    // shows the assistant's style and its safety framing.
    if (/\/admin\/intellicare$/.test(path)) {
      return {
        reply: 'Thank you for sharing this — here are a few thoughts as you weigh it up.\n\n'
          + '• A calm, unhurried one-to-one check-in, somewhere private, is often the best first step. '
          + 'Notice what the student says, and what they leave unsaid.\n'
          + '• Look for a pattern over time — sleep, appetite, friendships, participation — rather than a single moment.\n'
          + '• Loop in your wellness council early, and consider a gentle, non-stigmatising conversation with the family.\n\n'
          + 'If at any point you sense immediate risk — talk of self-harm, an unsafe home, a child in danger — '
          + 'treat it as urgent: follow your school’s safeguarding protocol and the helplines, and do not wait.\n\n'
          + 'This is a demo reply — sign in to the live portal to use Intellicare for real.',
      };
    }
    // Bulk teacher import in the demo — echo a believable summary.
    if (/\/admin\/teachers\/import$/.test(path)) {
      return {
        ok: true,
        total: 4,
        created: 3,
        skipped: [{ name: 'Anjali Mehta', email: 'anjali.mehta@dpsgurgaon.edu' }],
        errors: [],
      };
    }
    // Compliance input edit in the demo — mutate the in-memory store
    // so the editor reflects the change, then echo a fresh report.
    const cm = path.match(/\/admin\/compliance\/([^/]+)/);
    if (cm) {
      let p = {};
      try { p = init.body ? JSON.parse(init.body) : {}; } catch { p = {}; }
      if (p.mandateId) {
        demoComplianceInputs = {
          ...demoComplianceInputs,
          [p.mandateId]: {
            status: p.status || 'not_met',
            note: typeof p.note === 'string' ? p.note.trim() : '',
            updatedBy: 'demo@mysafespaces.org',
            updatedAt: new Date().toISOString(),
          },
        };
      }
      return demoCompliance();
    }
    // Session create / update / delete in the demo — echo a coherent
    // session so the manager UI stays well-formed after the call.
    const sm = path.match(/\/admin\/sessions(?:\/([^/]+))?$/);
    if (sm) {
      if (method === 'DELETE') return { ok: true };
      let p = {};
      try { p = init.body ? JSON.parse(init.body) : {}; } catch { p = {}; }
      if (sm[1]) {
        const base = demoSessions().find((s) => s.id === sm[1]) || demoSessions()[0];
        return { ...base, ...(p.status ? { status: p.status } : {}), attendees: base.attendees, stats: base.stats };
      }
      const label = p.programType === 'gatekeeper' ? 'Gatekeeper course'
        : p.programType === 'lab' ? 'Teacher Lab' : 'Workshop';
      return {
        id: `ses_demo${Date.now().toString(36)}`,
        schoolId: 'sch_001',
        programType: p.programType || 'workshop',
        programLabel: label,
        courseSlug: p.programType === 'gatekeeper' ? 'swiss-gatekeeper-foundations' : null,
        title: p.title || 'New session',
        facilitator: p.facilitator || '',
        scheduledAt: p.scheduledAt || new Date().toISOString(),
        audience: p.audience || 'teachers',
        surveyToken: 'demo',
        status: 'open',
        createdAt: new Date().toISOString(),
        createdBy: 'demo@mysafespaces.org',
        attendees: [],
        stats: { count: 0, avgRating: null, nps: null, recommendCount: 0 },
      };
    }
    // Anonymous-report mutations: return a coherent report shape so the
    // detail modal stays well-formed after a status change in the demo.
    const m = path.match(/\/admin\/anon-reports\/([^/]+)/);
    if (m) {
      const base = demoAnonReports().find((r) => r.id === m[1]) || demoAnonReports()[0];
      let patch = {};
      try { patch = init.body ? JSON.parse(init.body) : {}; } catch { patch = {}; }
      const next = { ...base, ...(patch.status ? { status: patch.status } : {}) };
      if (patch.status === 'evaluating') { next.acknowledgedAt = new Date().toISOString(); next.overdue = false; }
      if (patch.status === 'resolved' || patch.status === 'dismissed') next.resolvedAt = new Date().toISOString();
      next.statusHistory = [
        ...(base.statusHistory || []),
        { status: patch.status || base.status, at: new Date().toISOString(), by: 'demo@mysafespaces.org', note: patch.note || null },
      ];
      return next;
    }
    return { ok: true, id: 'demo' };
  }
  // Order matters — most specific path patterns first.
  if (/\/admin\/schools\/[^/]+\/cert/.test(path)) {
    return { eligible: false, blockers: [], teacherCount: 0, certifiedTeacherCount: 0, councilSize: 0, allPoliciesAdopted: false, adoptedPolicies: [], missingPolicies: [], policies: [] };
  }
  if (/\/admin\/schools\/[^/]+$/.test(path)) {
    return {
      id: 'sch_001',
      name: 'Delhi Public School, Gurgaon',
      shortName: 'DPS Gurgaon',
      tier: 'Premium',
      city: 'Gurgaon', state: 'Haryana', studentsCount: 1200,
      schoolYear: DEMO_SCHOOL_YEAR,
    };
  }
  if (/\/admin\/systems\/[^/]+\/rollup/.test(path)) return { schools: [], systemAdmins: [] };
  if (/\/admin\/schools\/[^/]+\/anon-notify/.test(path)) return { severities: ['critical', 'high'], recipientIds: ['cm_dm1', 'cm_dm2'] };
  if (/\/admin\/teachers/.test(path))    return [];
  if (/\/admin\/council/.test(path))     return demoCouncil();
  if (/\/admin\/phases\//.test(path))    return demoPhases();
  if (/\/admin\/incidents/.test(path))   return [];
  if (/\/admin\/lw-seats\//.test(path))  return null;
  if (/\/admin\/systems/.test(path))     return [];
  if (/\/admin\/resources/.test(path))   return [];
  if (/\/admin\/workshops/.test(path))   return [];
  if (/\/admin\/overview/.test(path)) {
    return {
      counts: {
        systems: 3, schools: 12, teachers: 168, certifiedTeachers: 121,
        nps: 54, npsRespondents: 96, npsPromoters: 61, npsDetractors: 9,
        avgRating: 4.6, surveyRespondents: 96, surveyEligible: 121,
      },
      systems: [],
      recentIncidents: [],
      anonReports: { total: 5, received: 2, open: 3, closed: 2, overdue: 1 },
    };
  }
  if (/\/admin\/admins/.test(path))      return [];
  if (/\/admin\/school-admins/.test(path)) return [];
  if (/\/admin\/session-rollup\//.test(path)) {
    return {
      totalTeachers: 4,
      teachers: { completedSessions: 1, teachersPresent: 3, totalTeachers: 4, percent: 75, lastCompletedAt: hAgo(118) },
      labs: { completedSessions: 0, teachersPresent: 0, totalTeachers: 4, percent: 0, lastCompletedAt: null },
    };
  }
  if (/\/admin\/sessions\/[^/]+$/.test(path)) {
    const sid = path.split('/').pop();
    return demoSessions().find((s) => s.id === sid) || demoSessions()[0];
  }
  if (/\/admin\/sessions/.test(path)) return demoSessions();
  if (/\/admin\/anon-reports/.test(path)) return demoAnonReports();
  if (/\/admin\/anon-metrics/.test(path)) return demoAnonMetrics();
  if (/\/admin\/anon-token\//.test(path)) return { token: 'demo' };
  if (/\/admin\/anon-config/.test(path)) return { slaHours: { critical: 24, high: 48, medium: 72, low: 120 } };
  if (/\/admin\/anon-overdue/.test(path)) return { total: 5, received: 2, open: 3, closed: 2, overdue: 1, bySchool: { sch_001: 1 } };
  if (/\/admin\/compliance\//.test(path)) return demoCompliance();
  if (/\/admin\/push-key/.test(path))    return { publicKey: '' };
  if (/\/admin\/login-events/.test(path)) return { events: [], days: 14 };
  if (/\/admin\/me/.test(path))          return { role: 'staff', email: 'demo@mysafespaces.org' };
  return [];
};

const apiFetch = async (path, init = {}) => {
  const token = getIdToken();
  if (!token) throw new Error('Not signed in.');

  // Demo session: skip the backend entirely. See isDemoToken comment.
  if (isDemoToken(token)) return demoStub(path, init);

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  if (!res.ok) {
    // 401 = JWT authorizer or Lambda role-check rejection. Treat as
    // "session ended for some reason" and surface it through the
    // session-expired event so AuthContext can show /login.
    if (res.status === 401) fireSessionExpired(path);
    const msg = body?.error || body?.message || `Admin API ${path} → ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
};

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries).toString();
};

// ─────────────────────────────────────────────────────────────
// School systems
// ─────────────────────────────────────────────────────────────
export const listSchoolSystems = () => apiFetch('/admin/systems');
export const getSchoolSystem = (id) => apiFetch(`/admin/systems/${encodeURIComponent(id)}`);
export const onboardSchoolSystem = (input) =>
  apiFetch('/admin/systems', { method: 'POST', body: JSON.stringify(input) });
export const updateSchoolSystem = (id, patch) =>
  apiFetch(`/admin/systems/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

// ─────────────────────────────────────────────────────────────
// Schools
// ─────────────────────────────────────────────────────────────
export const listSchools = (filters = {}) => apiFetch(`/admin/schools${qs(filters)}`);
export const getSchool = (id) => apiFetch(`/admin/schools/${encodeURIComponent(id)}`);
export const onboardSchool = (input) =>
  apiFetch('/admin/schools', { method: 'POST', body: JSON.stringify(input) });
export const setSchoolSystem = (schoolId, systemId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ systemId: systemId || null }),
  });
export const updateSchool = (id, patch) =>
  apiFetch(`/admin/schools/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * Ask My Safe Spaces to upgrade a school's programme tier. Body:
 * { requestedTier, message }. The request is stored on the school and
 * emailed to the SWISS team. Any admin scoped to the school may send.
 */
// ── SWISS Safe Hands (staff onboarding) ─────────────────────────
// /admin/schools/:id/safehands/... — gated server-side by the school's
// `safehands` add-on. See infrastructure/admin-lambda/safehands.js.
const shPath = (schoolId, rest) => `/admin/schools/${encodeURIComponent(schoolId)}/safehands/${rest}`;
export const getSafeHandsCatalog = (schoolId) => apiFetch(shPath(schoolId, 'catalog'));
export const getSafeHandsSettings = (schoolId) => apiFetch(shPath(schoolId, 'settings'));
export const saveSafeHandsSettings = (schoolId, body) =>
  apiFetch(shPath(schoolId, 'settings'), { method: 'PUT', body: JSON.stringify(body || {}) });
export const getSafeHandsSummary = (schoolId) => apiFetch(shPath(schoolId, 'summary'));
export const refreshSafeHands = (schoolId) => apiFetch(shPath(schoolId, 'refresh'), { method: 'POST' });
export const listSafeHandsStaff = (schoolId) => apiFetch(shPath(schoolId, 'staff'));
export const createSafeHandsStaff = (schoolId, body) =>
  apiFetch(shPath(schoolId, 'staff'), { method: 'POST', body: JSON.stringify(body || {}) });
export const bulkUploadSafeHandsStaff = (schoolId, rows) =>
  apiFetch(shPath(schoolId, 'staff/bulk'), { method: 'POST', body: JSON.stringify({ rows }) });
export const updateSafeHandsStaff = (schoolId, staffId, body) =>
  apiFetch(shPath(schoolId, `staff/${encodeURIComponent(staffId)}`), { method: 'PUT', body: JSON.stringify(body || {}) });
export const withdrawSafeHandsStaff = (schoolId, staffId, reason) =>
  apiFetch(shPath(schoolId, `staff/${encodeURIComponent(staffId)}/withdraw`), { method: 'POST', body: JSON.stringify({ reason }) });
export const syncSafeHandsStaff = (schoolId, staffId) =>
  apiFetch(shPath(schoolId, `staff/${encodeURIComponent(staffId)}/sync`), { method: 'POST' });
export const resendSafeHandsWelcome = (schoolId, staffId) =>
  apiFetch(shPath(schoolId, `staff/${encodeURIComponent(staffId)}/resend-welcome`), { method: 'POST' });
export const completeSafeHandsCourse = (schoolId, staffId, courseKey, body) =>
  apiFetch(shPath(schoolId, `staff/${encodeURIComponent(staffId)}/courses/${encodeURIComponent(courseKey)}/complete`), {
    method: 'POST', body: JSON.stringify(body || {}),
  });

export const requestSchoolUpgrade = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/upgrade-request`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });

/**
 * Per-school certification readiness. Returns:
 *   { eligible, blockers, teacherCount, certifiedTeacherCount,
 *     councilSize, councilFirstMeetingAt, allPoliciesAdopted,
 *     adoptedPolicies, missingPolicies, policies: [{key,label}] }
 */
export const getSchoolCert = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/cert`);

// ─────────────────────────────────────────────────────────────
// School admins
// ─────────────────────────────────────────────────────────────
export const listSchoolAdmins = (schoolId) => apiFetch(`/admin/school-admins${qs({ schoolId })}`);
export const onboardSchoolAdmin = (input) =>
  apiFetch('/admin/school-admins', { method: 'POST', body: JSON.stringify(input) });
export const sendPasswordResetLink = (schoolAdminId) =>
  apiFetch(`/admin/school-admins/${encodeURIComponent(schoolAdminId)}/reset-password`, {
    method: 'POST',
  });

// ─────────────────────────────────────────────────────────────
// Teachers
// ─────────────────────────────────────────────────────────────
export const listTeachers = (schoolId) => apiFetch(`/admin/teachers${qs({ schoolId })}`);
export const onboardTeacher = (input) =>
  apiFetch('/admin/teachers', { method: 'POST', body: JSON.stringify(input) });

/**
 * Bulk-onboard teachers from a CSV (columns: name, email, optional
 * subject + grade). A teacher already on the roster — including one
 * synced from LearnWorlds — is matched by email and skipped, never
 * duplicated. Returns { ok, total, created, skipped:[{name,email}],
 * errors:[{row,reason}] }.
 */
export const importTeachers = (schoolId, csv) =>
  apiFetch('/admin/teachers/import', {
    method: 'POST',
    body: JSON.stringify({ schoolId, csv }),
  });
export const setTeacherLwStatus = (teacherId, patch) =>
  apiFetch(`/admin/teachers/${encodeURIComponent(teacherId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * Preview which teachers would be deleted by a "delete-from-CSV"
 * upload. Returns {schoolId, total, matched, unmatched, blanks,
 * items:[{csvRow, csvName, csvEmail, matchedBy, teacher, warnings}]}
 * — pure preview, no mutation. The UI shows it as a confirmation
 * table with checkboxes; the caller then sends the picked ids to
 * deleteTeachersBulk.
 */
export const previewTeacherCsvDelete = (schoolId, csv) =>
  apiFetch('/admin/teachers/delete-preview', {
    method: 'POST',
    body: JSON.stringify({ schoolId, csv }),
  });

/**
 * Bulk-delete teachers by id. Pass the ids ticked from the preview.
 * Returns {deleted, requested, errors:[{id, reason}]}.
 */
export const bulkDeleteTeachers = (schoolId, teacherIds) =>
  apiFetch('/admin/teachers/delete-bulk', {
    method: 'POST',
    body: JSON.stringify({ schoolId, teacherIds }),
  });

/**
 * Auto-detected duplicate-teacher clusters for a school.
 * Returns [{ reason, key, members: [{id, name, email, progress,
 * certifiedAt, subject, grade, createdAt}] }] — `reason` is
 * 'same-email' (high confidence) or 'same-name' (medium). Members
 * are sorted with the best candidate primary first.
 */
export const listTeacherDuplicates = (schoolId) =>
  apiFetch(`/admin/teachers/duplicates${qs({ schoolId })}`);

/**
 * Merge `duplicateIds[]` into `primaryId`. Server rewrites every
 * reference (sessions / LW seats / school-admin links) before
 * deleting the duplicates. Returns
 * { primary, mergedCount, attendeeReassignments, seatReassignments,
 *   adminLinks }.
 */
export const mergeTeachers = ({ primaryId, duplicateIds }) =>
  apiFetch('/admin/teachers/merge', {
    method: 'POST',
    body: JSON.stringify({ primaryId, duplicateIds }),
  });

// ─────────────────────────────────────────────────────────────
// Survey CSV import + email aliases
// ─────────────────────────────────────────────────────────────

/**
 * Upload a survey-export CSV. Server matches each row by email
 * against the teacher roster (primary email + aliases) and stamps
 * rating + NPS + comment onto the matching course.
 *
 * Returns { ok, total, matched, unmatched } where unmatched is an
 * array of { email, rating, reason } so the caller can offer a
 * "Map to teacher" UI for the misses.
 */
export const uploadSurveyCsv = ({ csv, courseSlug }) =>
  apiFetch('/admin/survey-import', {
    method: 'POST',
    body: JSON.stringify({ csv, courseSlug }),
  });

/**
 * Attach an alternate (typically personal) email to a teacher row
 * so subsequent survey imports route there. The next import run
 * will pick the alias up automatically.
 */
export const addTeacherAlias = (teacherId, alias) =>
  apiFetch(`/admin/teacher-aliases/${encodeURIComponent(teacherId)}`, {
    method: 'POST',
    body: JSON.stringify({ alias }),
  });

// ─────────────────────────────────────────────────────────────
// Wellness council
// ─────────────────────────────────────────────────────────────
export const listCouncilMembers = (schoolId) => apiFetch(`/admin/council${qs({ schoolId })}`);
export const addCouncilMember = (input) =>
  apiFetch('/admin/council', { method: 'POST', body: JSON.stringify(input) });
export const removeCouncilMember = (id) =>
  apiFetch(`/admin/council/${encodeURIComponent(id)}`, { method: 'DELETE' });

export const listCouncilMeetings = (schoolId) => apiFetch(`/admin/council-meetings${qs({ schoolId })}`);
export const addCouncilMeeting = (input) =>
  apiFetch('/admin/council-meetings', { method: 'POST', body: JSON.stringify(input) });
export const removeCouncilMeeting = (id) =>
  apiFetch(`/admin/council-meetings/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Presign and PUT a Minutes of Meeting PDF; returns the `minutes` field for addCouncilMeeting. */
export const uploadCouncilMinutes = async (schoolId, file) => {
  const { url, key } = await apiFetch('/admin/council-meetings/presign', {
    method: 'POST',
    body: JSON.stringify({ schoolId, filename: file.name, contentType: file.type }),
  });
  if (!url) return null; // demo session — nothing to upload to
  const put = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!put.ok) throw new Error('Upload to storage failed.');
  return { filename: file.name, s3Key: key, size: file.size };
};
export const getCouncilMinutesUrl = (id) =>
  apiFetch(`/admin/council-meetings/${encodeURIComponent(id)}/minutes`);

// ─────────────────────────────────────────────────────────────
// Certification phases
// ─────────────────────────────────────────────────────────────
export const getSchoolPhases = (schoolId) => apiFetch(`/admin/phases/${encodeURIComponent(schoolId)}`);
export const updatePhase = (schoolId, phaseKey, patch) =>
  apiFetch(`/admin/phases/${encodeURIComponent(schoolId)}/${encodeURIComponent(phaseKey)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

// ─────────────────────────────────────────────────────────────
// LearnWorlds seat (per-school link)
// ─────────────────────────────────────────────────────────────
export const getSchoolLwSeat = (schoolId) =>
  apiFetch(`/admin/lw-seats/${encodeURIComponent(schoolId)}`);
export const linkSchoolLwSeat = (schoolId, seat) =>
  apiFetch(`/admin/lw-seats/${encodeURIComponent(schoolId)}`, {
    method: 'PUT',
    body: JSON.stringify(seat),
  });
export const unlinkSchoolLwSeat = (schoolId) =>
  apiFetch(`/admin/lw-seats/${encodeURIComponent(schoolId)}`, { method: 'DELETE' });

/**
 * Manually pull the latest user/progress data from LearnWorlds for one
 * school. Returns the sync summary `{ pulled, created, updated,
 * withProgress, hasProgress, courseTitle }` so callers can show a
 * meaningful toast.
 *
 * Nightly cron handles unattended catch-up; this is the "I just made
 * changes in LW and want to see them now" button.
 */
export const syncLwForSchool = (schoolId) =>
  apiFetch(`/admin/lw-sync/school/${encodeURIComponent(schoolId)}`, { method: 'POST' });

/**
 * MSS-only — run the nightly sync ad-hoc across every school with a
 * linked seat. Subject to the 30s API Gateway timeout so it can fail
 * on large fleets.
 */
export const syncLwForAllSchools = () =>
  apiFetch('/admin/lw-sync/all', { method: 'POST' });

// ─────────────────────────────────────────────────────────────
// Thrive LMS (learn.mysafespaces.org) — replaces LearnWorlds
// ─────────────────────────────────────────────────────────────
/**
 * The school's LMS institution link, plus a live `provisionedCount` /
 * `totalTeachers` snapshot computed from the current teacher roster —
 * always fresh, so the tab can show "38 of 45 provisioned" without the
 * admin having to open the provision modal.
 */
export const getSchoolLmsLink = (schoolId) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}`);

/** The LMS's assignable course catalog, for the provisioning course picker. */
export const listLmsCourses = () => apiFetch('/admin/lms-courses');

/**
 * Link a school to an EXISTING LMS institution by slug, instead of
 * provisioning creating a fresh swiss-<schoolId> one — for a school
 * that already went through an earlier migration under a different
 * slug (e.g. a one-off CSV import). Reuses that institution's real
 * membership/progress/certificates rather than forking off a second,
 * empty one. Read-only against the LMS; only writes SWISS's own link
 * pointer. Returns `{ institution, link }`.
 */
export const linkExistingLmsInstitution = (schoolId, institutionSlug) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}`, {
    method: 'PUT',
    body: JSON.stringify({ institutionSlug }),
  });

/**
 * Provision (or re-provision) a school into the LMS: find-or-create its
 * institution and batch-add teachers as members via the LMS's bulk-
 * import endpoint. Three mutually exclusive ways to pick who's targeted:
 *   - `csv` (raw text, the LMS's own Email/Name/User Type/Courses
 *     template): each row is found-or-created as a SWISS teacher at
 *     this school first, THEN provisioned — add + provision in one shot.
 *   - `teacherIds`: scope to specific existing SWISS teachers (e.g.
 *     adding one teacher to a newly-added course).
 *   - neither: every current teacher at the school.
 *
 * `enroll` mirrors the LMS's own upload UI toggle "Enrol users in the
 * listed courses (in addition to institution course assignments)" —
 * when on, each targeted teacher/row is directly enrolled in
 * `courseSlugs` (or a CSV row's own Courses column). `sendEmails`
 * mirrors its "Send welcome / enrolment emails via SES (suppression
 * list respected)" toggle. Both are LMS-side behavior; passing them
 * through here just means SWISS-driven provisioning doesn't lose them.
 *
 * Returns `{ institution, teacherResults, summary, warnings }` —
 * `summary` has the headline counts (teachersProvisioned, teachersFailed,
 * newLmsAccountsCreated, addedToThisSchool, alreadyExisted,
 * courseEnrollments: [{courseTitle, enrolled}], emailsSent, emailsFailed,
 * emailsSuppressed) and `teacherResults` has the per-teacher detail.
 */
export const provisionSchoolToLms = (schoolId, {
  userTypes, courseSlugs, teacherIds, enroll, sendEmails, csv,
} = {}) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/provision`, {
    method: 'POST',
    body: JSON.stringify({ userTypes, courseSlugs, teacherIds, enroll, sendEmails, csv }),
  });

/**
 * Every member currently provisioned to a school's LMS institution,
 * each with the courses they're assigned. Returns
 * `{ institutionId, members: [] }` — empty array if not provisioned yet.
 */
export const listLmsMembers = (schoolId) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/members`);

/**
 * Unprovision one or more members (by LMS userId): removes their
 * institution membership and revokes the course access this school
 * granted. Does NOT delete their LMS account or progress/certificate
 * history — only this school's relationship to them.
 */
export const removeLmsMembers = (schoolId, userIds) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/members/remove`, {
    method: 'POST',
    body: JSON.stringify({ userIds }),
  });

/**
 * Revoke one course's access for one or more members, without
 * touching their membership or other courses.
 */
export const revokeLmsMemberCourse = (schoolId, userIds, courseSlug) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/members/revoke-course`, {
    method: 'POST',
    body: JSON.stringify({ userIds, courseSlug }),
  });

/**
 * The LearnWorlds → LMS cutover for one school: provisions every current
 * teacher to the LMS (enrolled in the Gatekeeper Essentials course) and
 * backfills completion — certificate included — for anyone already
 * certified per SWISS's LW-synced record, so they don't redo training.
 * Does not touch the LearnWorlds link itself; unlink separately once
 * verified. Safe to re-run (e.g. after a teacher is newly certified in LW).
 *
 * Returns `{ provision: {...}, backfill: { eligible, backfilled,
 * alreadyCompleted, failed, results } }`.
 */
export const migrateSchoolFromLearnWorlds = (schoolId) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/migrate-from-lw`, { method: 'POST' });

/**
 * Pull the LMS's current per-course progress/completion for every
 * teacher at a school back into swiss-teachers — same "never regress"
 * merge migrateSchoolFromLearnWorlds uses. Standalone refresh, useful
 * once a school is off LearnWorlds entirely.
 */
export const syncSchoolFromLms = (schoolId) =>
  apiFetch(`/admin/lms-link/${encodeURIComponent(schoolId)}/sync`, { method: 'POST' });

// ─────────────────────────────────────────────────────────────
// Communication Center — bulk email + WhatsApp to a school's roster
// (or the whole fleet), from a template.
// ─────────────────────────────────────────────────────────────
export const listCommTemplates = () => apiFetch('/admin/comm/templates');

export const createCommTemplate = (input) =>
  apiFetch('/admin/comm/templates', { method: 'POST', body: JSON.stringify(input) });

export const updateCommTemplate = (id, patch) =>
  apiFetch(`/admin/comm/templates/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteCommTemplate = (id) =>
  apiFetch(`/admin/comm/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });

/**
 * Live recipient coverage for the compose screen, before a campaign is
 * created — how many teachers are eligible for the chosen channel vs.
 * missing a contact on file vs. opted out.
 * `params`: { scope: 'school'|'fleet', schoolId?, schoolIds?: string[], channel }
 */
export const previewCommRecipients = ({ scope, schoolId, schoolIds, channel }) => {
  const qs = new URLSearchParams({ scope, channel });
  if (schoolId) qs.set('schoolId', schoolId);
  if (schoolIds && schoolIds.length) qs.set('schoolIds', schoolIds.join(','));
  return apiFetch(`/admin/comm/recipients?${qs.toString()}`);
};

export const listCommCampaigns = (schoolId) =>
  apiFetch(`/admin/comm/campaigns${schoolId ? `?schoolId=${encodeURIComponent(schoolId)}` : ''}`);

export const getCommCampaign = (id) =>
  apiFetch(`/admin/comm/campaigns/${encodeURIComponent(id)}`);

/**
 * Create a draft campaign — resolves + snapshots recipients as queued
 * Send rows, but sends nothing yet. Body: { scope, schoolId?, schoolIds?,
 * channel, templateId, teacherIds? }.
 */
export const createCommCampaign = (input) =>
  apiFetch('/admin/comm/campaigns', { method: 'POST', body: JSON.stringify(input) });

/** Actually send (or retry the failed rows of) a draft/partial campaign. */
export const sendCommCampaign = (id) =>
  apiFetch(`/admin/comm/campaigns/${encodeURIComponent(id)}/send`, { method: 'POST' });

/** One real message to an address the caller supplies directly — for the "Send test" button. */
export const sendCommTest = ({ channel, templateId, to, schoolId }) =>
  apiFetch('/admin/comm/test-send', { method: 'POST', body: JSON.stringify({ channel, templateId, to, schoolId }) });

/** Live Meta WhatsApp template list + approval status. */
export const listMetaWhatsAppTemplates = () => apiFetch('/admin/comm/whatsapp-templates');

/** Submit a new WhatsApp template for Meta review — real, uses the account's template quota. */
export const createMetaWhatsAppTemplate = (input) =>
  apiFetch('/admin/comm/whatsapp-templates', { method: 'POST', body: JSON.stringify(input) });

export const deleteMetaWhatsAppTemplate = (name) =>
  apiFetch(`/admin/comm/whatsapp-templates/${encodeURIComponent(name)}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────
// Weekly Seat-Progress report
// ─────────────────────────────────────────────────────────────
/**
 * A school's training-progress report summary — teacher counts,
 * progress distribution, and the per-teacher breakdown. Computed
 * from the synced roster, so it always matches the dashboards.
 */
export const getSchoolSeatReport = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/seat-report`);

/**
 * Email a school's progress report now. `body` may carry `{ to, cc }`
 * recipient overrides; omit them to use the school's admins + the
 * configured global / per-school recipients.
 */
export const sendSchoolSeatReport = (schoolId, body = {}) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/seat-report/send`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** Weekly-report settings: { enabled, cc[], optOutSchoolIds[], perSchool }. */
export const getSeatReportConfig = () => apiFetch('/admin/seat-report-config');

/** Save weekly-report settings — MSS / super only. */
export const saveSeatReportConfig = (patch) =>
  apiFetch('/admin/seat-report-config', { method: 'PUT', body: JSON.stringify(patch) });

/** Recent weekly-report run history (newest first). */
export const getSeatReportRuns = () => apiFetch('/admin/seat-report-runs');

// ─────────────────────────────────────────────────────────────
// Assessments catalog — library of assessments that can be deployed
// to schools (MSS / super manage; group + school see what's in scope).
// ─────────────────────────────────────────────────────────────
/** List assessments visible to the caller (built-in + custom). */
export const listAssessmentsCatalog = () => apiFetch('/admin/assessments');

/** Fetch one assessment (built-in or custom). */
export const getAssessment = (id) =>
  apiFetch(`/admin/assessments/${encodeURIComponent(id)}`);

/** Create a custom assessment (MSS / super only). */
export const createAssessment = (body) =>
  apiFetch('/admin/assessments', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });

/** Update a custom assessment — title / description / scope / instrument. */
export const updateAssessment = (id, patch) =>
  apiFetch(`/admin/assessments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  });

/**
 * Update only the assignment fields of an assessment (Resources-style):
 * { global, groupIds[], schoolIds[] } — coexist freely. Doesn't touch
 * title / description / instrument / scoring.
 */
export const assignAssessment = (id, { global, groupIds, schoolIds }) =>
  apiFetch(`/admin/assessments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      global: !!global,
      groupIds: Array.isArray(groupIds) ? groupIds : [],
      schoolIds: Array.isArray(schoolIds) ? schoolIds : [],
    }),
  });

/** Delete a custom assessment (built-ins are protected server-side). */
export const removeAssessment = (id) =>
  apiFetch(`/admin/assessments/${encodeURIComponent(id)}`, { method: 'DELETE' });

/**
 * Import one or many Mosaic Portal assessments. Accepts either a
 * single assessment object or an array. Re-imports of the same
 * mosaicId update the existing SWISS row instead of duplicating.
 * Returns {total, created, updated, failed, results:[…]}.
 */
export const importAssessmentsFromMosaic = (items, scope = 'global') =>
  apiFetch('/admin/assessments/import-mosaic', {
    method: 'POST',
    body: JSON.stringify({
      items: Array.isArray(items) ? items : [items],
      scope,
    }),
  });

/**
 * Try the live Mosaic API connector. Currently stubbed server-side
 * (returns 501 until Mosaic credentials are wired up). Catch the
 * error and fall back to the paste-JSON path.
 */
export const fetchAssessmentsFromMosaic = () =>
  apiFetch('/admin/assessments/fetch-mosaic', { method: 'POST' });

// ─────────────────────────────────────────────────────────────
// Student Wellbeing Assessments
// ─────────────────────────────────────────────────────────────
/** A school's uploaded student roster. */
export const getAssessmentRoster = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/roster`);

/** Replace a school's student roster — `students` is [{ studentId, name, grade }]. */
export const uploadAssessmentRoster = (schoolId, students) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/roster`, {
    method: 'POST',
    body: JSON.stringify({ students }),
  });

/** The school's check-in campaigns (rounds), newest first. */
export const listAssessmentCampaigns = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/campaigns`);

/** Create a check-in campaign — returns it with a public token. */
export const createAssessmentCampaign = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/campaigns`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });

/** Open / close (or rename) a check-in campaign. */
export const updateAssessmentCampaign = (campaignId, patch) =>
  apiFetch(`/admin/assessment-campaigns/${encodeURIComponent(campaignId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * Check-in results for a school. Always returns aggregate data; the
 * per-student detail is included only when the caller is the school's
 * counsellor (`counsellorView` flags which).
 */
export const getAssessmentResults = (schoolId, { campaignId, assessmentId } = {}) => {
  const params = new URLSearchParams();
  if (campaignId) params.set('campaignId', campaignId);
  if (assessmentId) params.set('assessmentId', assessmentId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/results${qs}`);
};

/**
 * Per-student longitudinal trend across every assessment they have
 * completed at this school. Counsellor-only on the server — anyone
 * else gets 403. Returns
 *   { schoolId, student, entries:[], series:[{assessmentTitle, scoring, entries:[…], delta}] }
 * Used by the SEMH module's "track improvement over re-screens" view.
 */
export const getStudentAssessmentTrend = (schoolId, studentId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/assessment/student-trend?studentId=${encodeURIComponent(studentId)}`);

/**
 * At-a-glance dashboard for a licensed module (SEMH, LD, ...).
 * Aggregates every round of every instrument the school has licensed
 * under that module into headline tiles + per-instrument band
 * distributions + a per-round trend. No PII — pure counts.
 */
export const getModuleDashboard = (schoolId, moduleKey) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/module/${encodeURIComponent(moduleKey)}/dashboard`);

// ─────────────────────────────────────────────────────────────
// SWISS WellTrack (ScholarCare) — licensed counselor caseload module
// ─────────────────────────────────────────────────────────────
// Every call below is counsellor-only server-side (403 for anyone
// else) in addition to the standard schoolId tenant scoping — this is
// clinical/crisis data on minors, not just a licence-gated view.

/** The counsellor's caseload roster, sorted by name. */
export const listScholarcareStudents = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students`);

/** Register a new student — required: firstName, lastName, grade, referralSource. */
export const createScholarcareStudent = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** A student's profile + full assessment history, newest first. */
export const getScholarcareStudent = (schoolId, studentId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}`);

export const updateScholarcareStudent = (schoolId, studentId, patch) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * Submit a 36-item EWQ intake or re-assessment — `responses` is an
 * array of 36 raw Likert values (1-5). The server always recomputes
 * domainScores/ewqIndex/band/q23Alert; nothing derived is trusted
 * from the client. Updates the student's currentBand/latestEwqIndex.
 */
export const createScholarcareAssessment = (schoolId, studentId, responses) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}/assessments`, {
    method: 'POST',
    body: JSON.stringify({ responses }),
  });

export const listScholarcareAssessments = (schoolId, studentId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}/assessments`);

/** Log a counseling session for a student. */
export const createScholarcareSession = (schoolId, studentId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}/sessions`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const listScholarcareSessions = (schoolId, studentId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/students/${encodeURIComponent(studentId)}/sessions`);

/** The school-wide teacher referral queue. */
export const listScholarcareReferrals = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/referrals`);

export const createScholarcareReferral = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/referrals`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateScholarcareReferral = (schoolId, referralId, patch) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/referrals/${encodeURIComponent(referralId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * Ensure + return the school's self-serve teacher-referral token —
 * the public link (/scholarcare/refer/<token>) a school shares with
 * its teachers so they can submit a referral with no login. Same
 * lazily-generated-persistent-token pattern as the anon-report link.
 */
export const getScholarcareReferralToken = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/referral-token`);

/** Submit a crisis-response event report for a student. */
export const createScholarcareCrisisEvent = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/crisis-events`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const listScholarcareCrisisEvents = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/crisis-events`);

/**
 * De-identified aggregate rollup — band distribution + session/
 * referral/crisis counts. Powers both the WellTrack Dashboard's
 * admin overview and the Reports page (both roles; counsellor and
 * admin/council see the same numbers, framed differently).
 */
export const getScholarcareOverview = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/scholarcare/overview`);

/**
 * Recent LearnWorlds form webhook ingestions for the MSS admin's
 * "what's coming in" panel. Returns newest first.
 */
export const listLwWebhooks = ({ limit = 50 } = {}) =>
  apiFetch(`/admin/lw-webhooks?limit=${encodeURIComponent(limit)}`);

/**
 * Recent LMS webhook deliveries (enrollment/progress/certificate
 * events pushed from learn.mysafespaces.org) for the MSS admin's
 * "what's coming in" panel. Returns newest first. Pass `schoolId` to
 * scope to one school's events (unmatched events, e.g. a test-fire with
 * a fake email, never carry a schoolId and won't show under any school).
 */
export const listLmsWebhooks = ({ limit = 50, schoolId } = {}) =>
  apiFetch(`/admin/lms-webhooks?limit=${encodeURIComponent(limit)}${schoolId ? `&schoolId=${encodeURIComponent(schoolId)}` : ''}`);

/**
 * Public wellbeing page — MSS admin management endpoints.
 * The public page itself is served from /public/school/:slug/wellbeing
 * (see publicApi.getPublicSchoolWellbeing).
 */
export const updatePublicPage = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/public-page`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const listSchoolPolicies = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/policies`);

export const createSchoolPolicy = (schoolId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/policies`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateSchoolPolicy = (schoolId, policyId, body) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/policies/${encodeURIComponent(policyId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteSchoolPolicy = (schoolId, policyId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/policies/${encodeURIComponent(policyId)}`, {
    method: 'DELETE',
  });

/** Enable / disable the Assessments module for every school in a group. */
export const enableSystemAssessments = (systemId, enabled) =>
  apiFetch(`/admin/systems/${encodeURIComponent(systemId)}/assessment-enable`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });

/**
 * Designate (or clear) a school's counsellor by email. The counsellor —
 * a school admin or the group admin — is the only account that sees
 * individual student check-in results. Pass an empty email to clear it.
 */
export const setSchoolCounsellor = (schoolId, email) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/counsellor`, {
    method: 'PATCH',
    body: JSON.stringify({ email: email || '' }),
  });

// ─────────────────────────────────────────────────────────────
// Anonymous Reporting
// ─────────────────────────────────────────────────────────────

/** List a school's anonymous reports (newest first, each decorated
 *  with a computed `overdue` flag). */
export const listAnonReports = (schoolId) =>
  apiFetch(`/admin/anon-reports${qs({ schoolId })}`);

/** One anonymous report by id. */
export const getAnonReport = (id) =>
  apiFetch(`/admin/anon-reports/${encodeURIComponent(id)}`);

/** Update a report: { status?, note?, assignedTo? }. The
 *  received → evaluating transition is the manual "council picked
 *  this up" acknowledgement. */
export const updateAnonReport = (id, patch) =>
  apiFetch(`/admin/anon-reports/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** School-year metrics rollup for a school's anonymous reports. */
export const getAnonMetrics = (schoolId) =>
  apiFetch(`/admin/anon-metrics${qs({ schoolId })}`);

/** Ensure + fetch the school's public reporting token (used to build
 *  the /report/<token> URL and QR code). */
export const getAnonToken = (schoolId) =>
  apiFetch(`/admin/anon-token/${encodeURIComponent(schoolId)}`);

/** SLA windows (hours per severity). */
export const getAnonConfig = () => apiFetch('/admin/anon-config');

/** Update SLA windows — MSS / super admins only. */
export const updateAnonConfig = (patch) =>
  apiFetch('/admin/anon-config', { method: 'PATCH', body: JSON.stringify(patch) });

/** Anonymous-report summary scoped to the caller's role
 *  ({ total, received, open, closed, overdue, bySchool }). Feeds both
 *  the cross-dashboard overdue flag and the received/open/closed card. */
export const getAnonOverdue = () => apiFetch('/admin/anon-overdue');

/** CBSE / Supreme Court / NEP compliance report for one school. */
export const getCompliance = (schoolId) =>
  apiFetch(`/admin/compliance/${encodeURIComponent(schoolId)}`);

/** Record admin-entered status + evidence for a school-managed
 *  compliance mandate (one SWISS does not itself deliver — e.g. the
 *  counsellor appointment). Returns the recomputed report. */
export const updateComplianceInput = (schoolId, { mandateId, status, note }) =>
  apiFetch(`/admin/compliance/${encodeURIComponent(schoolId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ mandateId, status, note }),
  });

// ─────────────────────────────────────────────────────────────
// POSH / POCSO statutory compliance
// ─────────────────────────────────────────────────────────────

/** POSH or POCSO report for a school — committee roster + mandate
 *  checklist. `kind` is 'posh' or 'pocso'. */
export const getStatutory = (schoolId, kind) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/statutory/${kind}`);

/** Record status + evidence for a school-managed POSH / POCSO mandate.
 *  Returns the recomputed report. */
export const updateStatutoryInput = (schoolId, kind, { mandateId, status, note }) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/statutory/${kind}`, {
    method: 'PATCH',
    body: JSON.stringify({ mandateId, status, note }),
  });

/** Add a POSH IC / POCSO CPC member. `member` carries committee
 *  ('posh_ic' | 'pocso_cpc'), name, role, email, phone, external. */
export const addCommitteeMember = (schoolId, member) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/committee`, {
    method: 'POST',
    body: JSON.stringify(member),
  });

/** Remove a committee member by id. */
export const removeCommitteeMember = (memberId) =>
  apiFetch(`/admin/committee-members/${encodeURIComponent(memberId)}`, {
    method: 'DELETE',
  });

// ─────────────────────────────────────────────────────────────
// Physical Safety & Audit Suite
// ─────────────────────────────────────────────────────────────

/** The 7-area physical-safety audit report for a school. */
export const getAudit = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit`);

/** Set an audit area's status + evidence (auditor / MSS admin). */
export const updateAuditArea = (schoolId, { areaId, status, note }) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit`, {
    method: 'PATCH',
    body: JSON.stringify({ areaId, status, note }),
  });

/** Mint a presigned PUT URL for an audit-document upload. */
export const presignAuditDocument = (schoolId, { filename, contentType }) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit/presign`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });

/** Attach an uploaded document to an audit area. */
export const attachAuditDocument = (schoolId, doc) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit/document`, {
    method: 'POST',
    body: JSON.stringify(doc),
  });

/** Get a short-lived download URL for an audit document. */
export const getAuditDocumentUrl = (schoolId, docId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit/document/${encodeURIComponent(docId)}`);

/** Remove an audit document. */
export const deleteAuditDocument = (schoolId, docId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/audit/document/${encodeURIComponent(docId)}`, {
    method: 'DELETE',
  });

// ── CBSE mandatory public disclosure ──
// School admins maintain their own school's; MSS / group admins any
// school in scope. Returns { board, publicSlug, publicPageEnabled,
// catalogue, disclosure }.

const disclosurePath = (schoolId) =>
  `/admin/schools/${encodeURIComponent(schoolId)}/disclosure`;

export const getDisclosure = (schoolId) => apiFetch(disclosurePath(schoolId));

/** Save text fields, results, publish switch and per-document visibility. */
export const saveDisclosure = (schoolId, body) =>
  apiFetch(disclosurePath(schoolId), { method: 'PATCH', body: JSON.stringify(body) });

/** Presign, upload to S3, then attach the file to a checklist item. */
export const uploadDisclosureDocument = async (schoolId, docKey, file) => {
  const { url, key } = await apiFetch(`${disclosurePath(schoolId)}/presign`, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, contentType: file.type }),
  });
  const put = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!put.ok) throw new Error('Upload to storage failed.');
  return apiFetch(`${disclosurePath(schoolId)}/document`, {
    method: 'POST',
    body: JSON.stringify({
      docKey, filename: file.name, s3Key: key, contentType: file.type, size: file.size,
    }),
  });
};

export const getDisclosureDocumentUrl = (schoolId, docKey) =>
  apiFetch(`${disclosurePath(schoolId)}/document/${encodeURIComponent(docKey)}`);

export const deleteDisclosureDocument = (schoolId, docKey) =>
  apiFetch(`${disclosurePath(schoolId)}/document/${encodeURIComponent(docKey)}`, {
    method: 'DELETE',
  });

// ── 3rd-party Auditors — MSS admin management ──

/** List all 3rd-party auditor accounts. */
export const listAuditors = () => apiFetch('/admin/auditors');

/** Invite a 3rd-party auditor. `input` = { name, email, schoolIds }. */
export const onboardAuditor = (input) =>
  apiFetch('/admin/auditors', { method: 'POST', body: JSON.stringify(input) });

/** Update an auditor — { name?, schoolIds? }. */
export const updateAuditor = (id, patch) =>
  apiFetch(`/admin/auditors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** Remove an auditor account. */
export const removeAuditor = (id) =>
  apiFetch(`/admin/auditors/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────
// Facilitators (session / workshop presenters)
// ─────────────────────────────────────────────────────────────

/** List the facilitator directory — used by the session form's
 *  facilitator type-ahead. Open to any authenticated portal user. */
export const listFacilitators = () => apiFetch('/admin/facilitators');

/** Add a facilitator. `input` = { name, email, phone, organization, role, notes }. */
export const addFacilitator = (input) =>
  apiFetch('/admin/facilitators', {
    method: 'POST',
    body: JSON.stringify(input),
  });

/** Update a facilitator (any of the fields above). */
export const updateFacilitator = (id, patch) =>
  apiFetch(`/admin/facilitators/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** Remove a facilitator from the directory. */
export const removeFacilitator = (id) =>
  apiFetch(`/admin/facilitators/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Set a school's academic year. First write is open to any admin
 *  scoped to the school; once set, the backend locks it to MSS. */
export const setSchoolYear = (schoolId, { startDate, endDate }) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/school-year`, {
    method: 'PATCH',
    body: JSON.stringify({ startDate, endDate }),
  });

/** Per-school anonymous-report notification config:
 *  { severities: [...], recipientIds: [councilMemberId...] }. */
export const getAnonNotify = (schoolId) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/anon-notify`);
export const updateAnonNotify = (schoolId, patch) =>
  apiFetch(`/admin/schools/${encodeURIComponent(schoolId)}/anon-notify`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

// ─────────────────────────────────────────────────────────────
// In-person sessions (QR attendance + feedback survey)
// ─────────────────────────────────────────────────────────────

/** List a school's in-person sessions, decorated with attendees + stats. */
export const listSessions = (schoolId) =>
  apiFetch(`/admin/sessions${qs({ schoolId })}`);

/** One session by id (attendees + stats). */
export const getSession = (id) =>
  apiFetch(`/admin/sessions/${encodeURIComponent(id)}`);

/** Create an in-person session — mints the survey token for the QR.
 *  input: { schoolId, programType, title, facilitator?, scheduledAt?,
 *           audience?, courseSlug? } */
export const createSession = (input) =>
  apiFetch('/admin/sessions', { method: 'POST', body: JSON.stringify(input) });

/** Update a session — open/close, edit details. */
export const updateSession = (id, patch) =>
  apiFetch(`/admin/sessions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** Delete a session. */
export const deleteSession = (id) =>
  apiFetch(`/admin/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Per-phase in-person attendance rollup for the Progress views:
 *  { totalTeachers, teachers:{completedSessions,teachersPresent,
 *  totalTeachers,percent}, labs:{...} }. */
export const getSessionRollup = (schoolId) =>
  apiFetch(`/admin/session-rollup/${encodeURIComponent(schoolId)}`);

/** Promote a self-added (unrostered) session attendee onto the teacher
 *  roster and link their check-in.
 *  payload: { name, submittedAt, email?, subject?, grade? }. */
export const promoteSessionAttendee = (sessionId, payload) =>
  apiFetch(`/admin/sessions/${encodeURIComponent(sessionId)}/promote-attendee`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ─────────────────────────────────────────────────────────────
// Session polls (pre / post / pulse surveys, tracked per session)
// ─────────────────────────────────────────────────────────────

/** List a session's polls — each decorated with a per-question
 *  summary and its responses. */
export const listSessionPolls = (sessionId) =>
  apiFetch(`/admin/session-polls${qs({ sessionId })}`);

/** Create a poll on a session.
 *  input: { sessionId, title, kind?, questions: [
 *           { type, label, required?, options? } ] }. */
export const createSessionPoll = (input) =>
  apiFetch('/admin/session-polls', { method: 'POST', body: JSON.stringify(input) });

/** Update a poll — title, kind, status (open/closed) and/or questions. */
export const updateSessionPoll = (id, patch) =>
  apiFetch(`/admin/session-polls/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** Delete a poll and its responses. */
export const deleteSessionPoll = (id) =>
  apiFetch(`/admin/session-polls/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────
// Intellicare — reflective AI aide for school wellbeing staff
// ─────────────────────────────────────────────────────────────

/** Send the Intellicare conversation and get the assistant's reply.
 *  messages: [{ role: 'user' | 'assistant', content }]. Returns
 *  { reply }. */
export const intellicareChat = (messages) =>
  apiFetch('/admin/intellicare', { method: 'POST', body: JSON.stringify({ messages }) });

// ─────────────────────────────────────────────────────────────
// Web Push
// ─────────────────────────────────────────────────────────────

/** VAPID public key — the browser needs it to create a subscription. */
export const getPushKey = () => apiFetch('/admin/push-key');

/** Register this browser's push subscription, scoped server-side to
 *  the caller's role so report alerts target the right schools. */
export const pushSubscribe = (subscription) =>
  apiFetch('/admin/push-subscribe', { method: 'POST', body: JSON.stringify({ subscription }) });

/** Drop a push subscription by endpoint. */
export const pushUnsubscribe = (endpoint) =>
  apiFetch('/admin/push-unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });

// ─────────────────────────────────────────────────────────────
// Crisis incidents
// ─────────────────────────────────────────────────────────────
export const listIncidents = ({ schoolId, status } = {}) =>
  apiFetch(`/admin/incidents${qs({ schoolId, status })}`);
export const getIncident = (id) => apiFetch(`/admin/incidents/${encodeURIComponent(id)}`);
export const createIncident = (input) =>
  apiFetch('/admin/incidents', { method: 'POST', body: JSON.stringify(input) });
export const updateIncident = (id, patch) =>
  apiFetch(`/admin/incidents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
export const addIncidentAction = (id, action) =>
  apiFetch(`/admin/incidents/${encodeURIComponent(id)}/actions`, {
    method: 'POST',
    body: JSON.stringify(action),
  });

// ─────────────────────────────────────────────────────────────
// MSS admins (allowlist + invites)
// ─────────────────────────────────────────────────────────────
export const listAdmins = () => apiFetch('/admin/admins');
export const inviteAdmin = (input) =>
  apiFetch('/admin/admins', { method: 'POST', body: JSON.stringify(input) });
export const updateAdmin = (id, patch) =>
  apiFetch(`/admin/admins/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
export const removeAdmin = (id) =>
  apiFetch(`/admin/admins/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ─────────────────────────────────────────────────────────────
// System admins
// ─────────────────────────────────────────────────────────────
export const listSystemAdmins = (systemId) =>
  apiFetch(`/admin/systems/${encodeURIComponent(systemId)}/admins`);
export const onboardSystemAdmin = (systemId, input) =>
  apiFetch(`/admin/systems/${encodeURIComponent(systemId)}/admins`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
export const sendSystemAdminResetLink = (id) =>
  apiFetch(`/admin/system-admins/${encodeURIComponent(id)}/reset-password`, { method: 'POST' });

// ─────────────────────────────────────────────────────────────
// Resources (uploaded files: docs, posters, ebooks, videos)
// ─────────────────────────────────────────────────────────────

/** List resources visible to the caller. */
export const listResources = () => apiFetch('/admin/resources');

/**
 * Two-step upload — keeps Lambda out of the byte path:
 *   1. ask the API for a presigned PUT URL (this call)
 *   2. fetch() PUT the file straight to S3 (caller does this)
 *   3. POST /admin/resources to register the metadata
 *
 * The caller composes these into uploadResource() below.
 */
export const presignResourceUpload = ({ filename, contentType }) =>
  apiFetch('/admin/resources/presign', {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });

/** Register a resource after the file has been PUT to S3. */
export const createResource = (input) =>
  apiFetch('/admin/resources', { method: 'POST', body: JSON.stringify(input) });

/** Delete a resource (also removes the S3 object). */
export const deleteResource = (id) =>
  apiFetch(`/admin/resources/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Edit title / description / kind on an existing resource. */
export const updateResource = (id, patch) =>
  apiFetch(`/admin/resources/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/** Get a short-lived signed URL the browser can navigate to / download. */
export const getResourceDownloadUrl = (id) =>
  apiFetch(`/admin/resources/${encodeURIComponent(id)}/download`);

/**
 * High-level helper: take a File from an <input type=file>, upload to
 * S3, then register the metadata. Returns the registered resource row.
 */
export const uploadResource = async ({ file, title, description, kind, onProgress }) => {
  const { url, key, bucket } = await presignResourceUpload({
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  // Upload bytes straight to S3 from the browser.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('S3 upload failed (network)'));
    xhr.send(file);
  });
  // A new resource is created unassigned — the admin shares it later
  // with the Assign dialog (any number of groups / schools, or global).
  return createResource({
    title: title || file.name,
    description: description || '',
    kind: kind || undefined, // server falls back to MIME-derived if omitted
    s3Key: key,
    s3Bucket: bucket,
    mime: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    filename: file.name,
  });
};

// ─────────────────────────────────────────────────────────────
// Workshops (for teachers / parents / students) + media
// ─────────────────────────────────────────────────────────────

export const listWorkshops = () => apiFetch('/admin/workshops');

export const createWorkshop = (input) =>
  apiFetch('/admin/workshops', { method: 'POST', body: JSON.stringify(input) });

export const updateWorkshop = (id, patch) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const deleteWorkshop = (id) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(id)}`, { method: 'DELETE' });

const presignWorkshopMedia = (workshopId, { filename, contentType }) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(workshopId)}/media/presign`, {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  });

const registerWorkshopMedia = (workshopId, payload) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(workshopId)}/media`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * High-level helper — same pattern as uploadResource. Browser PUTs
 * to S3 directly, then registers the metadata.
 */
export const uploadWorkshopMedia = async ({ workshopId, file, caption, onProgress }) => {
  const { url, key, bucket } = await presignWorkshopMedia(workshopId, {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('S3 upload failed (network)'));
    xhr.send(file);
  });
  return registerWorkshopMedia(workshopId, {
    s3Key: key,
    s3Bucket: bucket,
    mime: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    filename: file.name,
    caption: caption || '',
  });
};

export const getWorkshopMediaUrl = (workshopId, mediaId) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(workshopId)}/media/${encodeURIComponent(mediaId)}`);

export const removeWorkshopMedia = (workshopId, mediaId) =>
  apiFetch(`/admin/workshops/${encodeURIComponent(workshopId)}/media/${encodeURIComponent(mediaId)}`, {
    method: 'DELETE',
  });

// ─────────────────────────────────────────────────────────────
// Identity
// ─────────────────────────────────────────────────────────────
export const getMe = () => apiFetch('/admin/me');

// ─────────────────────────────────────────────────────────────
// Login tracking
// ─────────────────────────────────────────────────────────────

/**
 * Record this sign-in. Called from AuthContext after Cognito returns
 * a valid token AND when an existing session is restored on page-load,
 * with a per-tab sessionStorage flag so refreshes within one tab
 * don't double-count.
 *
 * - keepalive:true so the POST survives the SPA navigation that
 *   typically follows sign-in.
 * - Failures swallowed — telemetry must never block sign-in.
 */
const LOGIN_RECORDED_KEY = 'swiss_login_recorded';

export const recordLogin = async () => {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOGIN_RECORDED_KEY)) {
      // Already recorded for this tab/session.
      return null;
    }
    const token = getIdToken();
    if (!token) return null;

    const res = await fetch(`${BASE}/admin/login-event`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    });
    if (!res.ok) {
      console.warn('[recordLogin] server returned', res.status);
      return null;
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(LOGIN_RECORDED_KEY, '1');
    }
    return res.json().catch(() => ({ ok: true }));
  } catch (err) {
    console.warn('[recordLogin] non-fatal', err.message);
    return null;
  }
};

/** Recent sign-ins for the Activity tab. Admin-only on the server. */
export const listLoginEvents = ({ days, limit } = {}) =>
  apiFetch(`/admin/login-events${qs({ days, limit })}`);

// ─────────────────────────────────────────────────────────────
// Aggregates
// ─────────────────────────────────────────────────────────────
export const getAdminOverview = () => apiFetch('/admin/overview');

/**
 * MSS-wide SEMH dashboard data — { totals, rows[] } where each row
 * carries a school's SEMH licence state + SSWS activity counts.
 * Drives the dedicated SEMH Screening tab.
 */
export const getSemhOverview = () => apiFetch('/admin/semh/overview');

/**
 * Same shape as the SEMH overview but for the Learning Disabilities
 * (MDSI + MAAS) module.
 */
export const getLdOverview = () => apiFetch('/admin/ld/overview');
export const getSystemRollup = (systemId) =>
  apiFetch(`/admin/systems/${encodeURIComponent(systemId)}/rollup`);

// Kept for backwards compat with the older localStorage-backed module.
// No-op in the production build; everything lives in DynamoDB now.
export const resetAdminStore = () => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('swiss_admin_store_v1');
    window.location.reload();
  }
};
